import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Download, FolderLock, AlertTriangle, ShieldCheck, Copy } from "lucide-react";

const CATS = ["case_documents", "documents_for_service", "supporting_evidence", "affidavit_return", "photos_evidence", "completed_documents"];

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

export default function AdminDocuments() {
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [audit, setAudit] = useState(null);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    if (status !== "all") params.status = status;
    api.get("/admin/documents", { params }).then(({ data }) => setDocs(data)).catch(() => {});
  };
  useEffect(load, [category, status]);

  const openAudit = (d) => {
    setAudit({ doc: d, events: [] });
    api.get("/admin/documents/audit", { params: { document_id: d.id } }).then(({ data }) => setAudit({ doc: d, events: data })).catch(() => {});
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900"><FolderLock className="h-6 w-6 text-slate-400" /> Documents</h2>
        <p className="text-slate-500">Search document metadata, versions and audit events across all cases.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search filename… (Enter)" className="pl-9" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="doc-category-filter" className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any category</SelectItem>
            {CATS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="doc-status-filter" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Filename</th><th className="px-4 py-3">Job #</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Ver</th><th className="px-4 py-3">Integrity</th>
              <th className="px-4 py-3">Uploaded by</th><th className="px-4 py-3">Uploaded</th><th className="px-4 py-3">SHA-256</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {docs.map((d) => {
              const flagged = d.integrity_flagged || d.integrity_status === "mismatch";
              return (
                <tr key={d.id} data-testid={`admin-doc-row-${d.id}`} className={`hover:bg-slate-50 ${flagged ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{d.original_filename}</td>
                  <td className="px-4 py-3">
                    {d.job_number ? (
                      <button data-testid={`doc-job-link-${d.id}`} onClick={() => nav(`/admin/jobs/${d.order_id}`)} className="font-mono-arps font-semibold text-indigo-600 hover:underline">{d.job_number}</button>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{d.category?.replace(/_/g, " ") || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{d.version != null ? `v${d.version}` : "—"}</td>
                  <td className="px-4 py-3">
                    {flagged ? (
                      <span data-testid={`doc-integrity-${d.id}`} title={d.integrity_reason || "Integrity mismatch — review required."} className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                        <AlertTriangle className="h-3.5 w-3.5" /> {d.integrity_status || "mismatch"} · review
                      </span>
                    ) : d.integrity_status === "verified" ? (
                      <span data-testid={`doc-integrity-${d.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> verified</span>
                    ) : <span className="text-xs text-slate-400">not checked</span>}
                    {flagged && d.integrity_reason && <p className="mt-1 max-w-[13rem] text-[10px] leading-tight text-red-600">{d.integrity_reason}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{d.uploaded_by || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(d.created_at)}</td>
                  <td className="px-4 py-3">
                    {d.sha256 ? (
                      <button data-testid={`doc-hash-${d.id}`} title={`Click to copy full SHA-256:\n${d.sha256}`} onClick={() => { navigator.clipboard?.writeText(d.sha256); toast.success("Full SHA-256 copied"); }} className="inline-flex items-center gap-1 font-mono-arps text-xs text-slate-500 hover:text-slate-900">
                        {d.sha256.slice(0, 10)}… <Copy className="h-3 w-3" />
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <a href={`${API}/files/${d.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="h-3.5 w-3.5" /></Button></a>
                      <Button data-testid={`doc-audit-${d.id}`} size="sm" variant="outline" onClick={() => openAudit(d)}>Audit</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {docs.length === 0 && <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400">No documents match.</td></tr>}
          </tbody>
        </table>
      </div>

      {audit && (
        <div data-testid="doc-audit-panel" className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-slate-900">Audit — {audit.doc.original_filename}</h3>
            <button onClick={() => setAudit(null)} className="text-sm text-slate-400 hover:text-slate-700">Close</button>
          </div>
          <ul className="space-y-1.5 text-xs text-slate-500">
            {audit.events.map((e, i) => (
              <li key={i}>{new Date(e.at).toLocaleString()} — <b>{e.action}</b> by {e.actor} ({e.role})</li>
            ))}
            {audit.events.length === 0 && <li className="text-slate-400">No audit events recorded.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
